package com.elan.edubridge.shared.domain.repository

import com.elan.edubridge.shared.domain.model.AgeGroup
import com.elan.edubridge.shared.domain.model.AuthResult
import com.elan.edubridge.shared.domain.model.AccountDeletionState
import com.elan.edubridge.shared.domain.model.ProfileUpdateResult
import com.elan.edubridge.shared.domain.model.SessionState
import kotlinx.coroutines.flow.StateFlow

/**
 * 세션 계약. 구현은 data 레이어에 있고 UI는 이 인터페이스만 안다.
 */
interface AccountRepository {
    /** 로그인·로그아웃·학원 전환·동의 상태가 모두 이 스트림으로 흐른다. */
    val session: StateFlow<SessionState>

    /** Flow를 쓰기 어려운 호출부(Swift 등)를 위한 스냅샷. */
    fun currentSession(): SessionState

    /**
     * 앱 시작 시 1회. 저장된 인증 상태를 복원한다.
     * 공급자의 비동기 복원(`awaitAuthReady`)을 기다린 뒤 세션을 발행하므로
     * 이것이 끝나기 전에 첫 화면을 그리면 새로고침마다 로그인으로 튕긴다(§S4).
     */
    suspend fun restoreSession(): SessionState

    suspend fun login(email: String, password: String): AuthResult

    /** 공개 가입 계정의 기본 역할은 항상 STUDENT다. 역할 인자를 받지 않는다. */
    suspend fun register(
        email: String,
        password: String,
        name: String,
        birthDate: String,
        ageGroup: AgeGroup = AgeGroup.UNKNOWN,
    ): AuthResult

    /**
     * Google 로그인. 성공하면 [session]이 새 상태를 방출한다.
     *
     * [idToken]은 플랫폼 차이를 흡수하는 인자다 — 웹은 팝업이 토큰까지 처리하므로 `null`,
     * Android/iOS는 플랫폼 SDK로 먼저 받은 Google ID 토큰을 넘긴다
     * (`AccountDataSource.signInWithGoogle` KDoc 참고).
     *
     * 기본 인자를 두지 않는다 — Kotlin 기본값은 ObjC 헤더에 노출되지 않아 Swift에서 사라진다.
     */
    suspend fun signInWithGoogle(
        idToken: String?,
        registrationName: String?,
        birthDate: String?,
    ): AuthResult

    suspend fun logout()

    /** 현재 로그인한 사용자의 표시 이름을 갱신하고 성공 시 세션에도 즉시 반영한다. */
    suspend fun updateMyProfile(name: String): ProfileUpdateResult

    /** 서버가 정본인 계정 삭제 유예 상태와 삭제 차단 사유를 조회한다. */
    suspend fun accountDeletionStatus(): AccountDeletionState

    /** 7일 유예 삭제 요청. reason은 입력하지 않으면 null을 넘긴다. */
    suspend fun requestAccountDeletion(reason: String?): AccountDeletionState

    /** 유예 중인 삭제 요청만 취소한다. */
    suspend fun cancelAccountDeletion(): AccountDeletionState

    /** 활성 학원 전환. 클라이언트 로컬 동작이므로 suspend가 아니다. */
    fun selectAcademy(academyId: String?)

    /**
     * 멤버십 재조회. 초대 수락·역할 부여 직후, 그리고 `permission-denied`를 받았을 때 호출한다.
     * 미러가 낡아 인가와 어긋난 경우의 정상 복구 경로다(§9.3-2).
     */
    suspend fun refreshMemberships(): SessionState

    /**
     * ID 토큰을 강제 갱신하고 멤버십을 다시 읽는다.
     *
     * custom claim은 최대 1시간 캐시된다(R11). 학원 승인 직후 이것 없이는 반드시 실패한다.
     */
    suspend fun forceRefreshToken(): SessionState
}
